import { z } from 'zod';
import { DBStrategy } from 'shared/src/types';
import { ISODateString } from 'shared/src/date-string-utils/types';

export const StrategyApiResponse = DBStrategy.omit({ start_date: true, end_date: true })
  .extend({
    planned_margin: z.coerce.number().nullable().meta({
      description: 'Price-weighted average of planned margins from line items'
    }),
    target_margin: z.coerce.number().nullable().meta({
      description: 'Target margin percentage set at strategy level'
    }),
    agency_markup_amount: z.coerce.number().default(0).meta({
      description: 'Calculated markup amount (price * agency_markup_rate)'
    }),
    start_date: ISODateString.nullable().meta({
      description: 'Proposal start date'
    }),
    end_date: ISODateString.nullable().meta({
      description: 'Proposal end date'
    })
  })
  .meta({
    id: 'StrategyApiResponse',
    description: 'Strategy with calculated fields including margins and markup'
  });

export type StrategyApiResponse = z.infer<typeof StrategyApiResponse>;

export const StrategyApiUpdateRequest = z
  .object({
    start_date: ISODateString.meta({
      description: 'Proposal/execution plan start date'
    }),
    end_date: ISODateString.meta({
      description: 'Proposal/execution plan end date'
    }),
    price: z.number().meta({
      description: 'Total strategy price (gross or net depending on pricing_mode)'
    }),
    referral_commission_rate: z.number().meta({
      description: 'Commission rate as decimal (e.g., 0.15 for 15%)'
    }),
    agency_markup_rate: z.number().meta({
      description: 'Agency markup rate as decimal (e.g., 0.20 for 20%)'
    }),
    pricing_mode: z.enum(['price', 'gross_price']).meta({
      description: 'Whether price represents net price or gross price'
    })
  })
  .partial()
  .meta({
    id: 'StrategyApiUpdateRequest',
    description: 'Strategy update fields (all optional for partial updates)'
  });

export type StrategyApiUpdateRequest = z.infer<typeof StrategyApiUpdateRequest>;

export const StrategyApiListRequest = z
  .object({
    page: z.coerce.number().int().min(1).default(1).meta({
      description: 'Page number (1-based)'
    }),
    pageSize: z.coerce.number().int().min(1).max(500).default(50).meta({
      description: 'Items per page (default 50, max 50)'
    })
  })
  .meta({
    id: 'StrategyApiListRequest',
    description: 'Simple pagination parameters'
  });

export type StrategyApiListRequest = z.infer<typeof StrategyApiListRequest>;

export const StrategyApiPromoteRequest = z
  .object({
    description: z.string().meta({
      description: 'User comment describing the changes being promoted'
    })
  })
  .meta({
    id: 'ProposalPromoteApiRequest',
    description: 'Request to promote a proposal to execution'
  });

export type ProposalPromoteApiRequest = z.infer<typeof StrategyApiPromoteRequest>;

export const StrategyPromoteApiResponse = StrategyApiResponse.extend({
  has_changes: z.boolean().meta({
    description: 'Indicates if promotion resulted in changes'
  })
}).meta({
  id: 'StrategyPromoteApiResponse',
  description: 'Proposal with promotion result indicator'
});

export type StrategyPromoteApiResponse = z.infer<typeof StrategyPromoteApiResponse>;

export const FieldChangeApi = z
  .object({
    field: z.string().meta({
      description: 'Name of the field that changed'
    }),
    old: z.unknown().meta({
      description: 'Previous value (null for new items)'
    }),
    new: z.unknown().meta({
      description: 'New value (null for deletions)'
    })
  })
  .meta({
    id: 'FieldChangeApi',
    description: 'Represents a change to a single field with old and new values'
  });
export type FieldChangeApi = z.infer<typeof FieldChangeApi>;

export const StrategyDiffApi = z
  .object({
    id: z.uuid().meta({
      description: 'Strategy identifier'
    }),
    exists_in_execution: z.boolean().meta({
      description: 'Whether strategy exists in execution schema'
    }),
    field_changes: z.array(FieldChangeApi).meta({
      description: 'Field-level changes (empty if no changes)'
    })
  })
  .meta({
    id: 'StrategyDiffApi',
    description:
      'Field-level changes for a strategy. When exists_in_execution is false, this represents a new strategy creation. Check field_changes.length > 0 to determine if there are changes.'
  });
export type StrategyDiffApi = z.infer<typeof StrategyDiffApi>;

export const LineItemToCreateApi = z
  .object({
    id: z.uuid().meta({
      description: 'Line item identifier'
    }),
    name: z.string().meta({
      description: 'Line item name'
    }),
    field_changes: z.array(FieldChangeApi).meta({
      description: 'All fields being set (old values are null)'
    })
  })
  .meta({
    id: 'LineItemToCreateApi',
    description:
      'Line item that will be created during promotion. All field_changes have old: null.'
  });
export type LineItemToCreateApi = z.infer<typeof LineItemToCreateApi>;

export const LineItemToUpdateApi = z
  .object({
    id: z.uuid().meta({
      description: 'Line item identifier'
    }),
    name: z.string().meta({
      description: 'Line item name'
    }),
    field_changes: z.array(FieldChangeApi).meta({
      description: 'Only fields that differ from execution'
    })
  })
  .meta({
    id: 'LineItemToUpdateApi',
    description: 'Line item that will be updated during promotion with field-level changes'
  });
export type LineItemToUpdateApi = z.infer<typeof LineItemToUpdateApi>;

export const LineItemToDeleteApi = z
  .object({
    id: z.uuid().meta({
      description: 'Line item identifier'
    }),
    name: z.string().meta({
      description: 'Line item name'
    }),
    blocked_by_media_buys: z.boolean().meta({
      description: 'If true, deletion will fail due to existing media plans'
    })
  })
  .meta({
    id: 'LineItemToDeleteApi',
    description: 'Line item that will be deleted during promotion with deletion metadata'
  });
export type LineItemToDeleteApi = z.infer<typeof LineItemToDeleteApi>;

export const LineItemChangesApi = z.object({
  to_create: z.array(LineItemToCreateApi).meta({
    description: 'Line items that will be created'
  }),
  to_update: z.array(LineItemToUpdateApi).meta({
    description: 'Line items that will be updated'
  }),
  to_delete: z.array(LineItemToDeleteApi).meta({
    description: 'Line items that will be deleted'
  })
});
export type LineItemChangesApi = z.infer<typeof LineItemChangesApi>;

export const ProposalPromoteDiffApiResponse = z
  .object({
    strategy: StrategyDiffApi,
    line_items: LineItemChangesApi
  })
  .meta({
    id: 'ProposalPromoteDiffApiResponse',
    description: 'Field-level differences between proposal and execution schemas'
  });
export type ProposalPromoteDiffApiResponse = z.infer<typeof ProposalPromoteDiffApiResponse>;
